import React, { useEffect, useRef } from 'react';
import vegaEmbed from 'vega-embed';

const ReportTimeline = ({ reportData }) => {
    const containerRef = useRef(null);

    const height = 50;

    useEffect(() => {
        // console.log('ReportTimeline - reportData:', reportData);
        // console.log('ReportTimeline - reportTimeline:', JSON.stringify(reportData?.reportTimeline, null, 2));
        
        if (!reportData?.reportTimeline || !containerRef.current) {
            console.log('ReportTimeline - Missing data or container');
            return;
        }

        // Extract timeline events
        const events = reportData.reportTimeline;
        // console.log('ReportTimeline - events:', events);

        // Find the unit back in service event to truncate timeline
        const backInServiceEvent = events.find(e => e.eventType === 'TIME' && e.data?.specification === 'UNIT_IN_SERVICE');
        
        // Filter events to only include those before or at the unit back in service time
        const filteredEvents = backInServiceEvent 
            ? events.filter(e => e.timeStamp <= backInServiceEvent.timeStamp)
            : events;
            
        console.log('ReportTimeline - filtered events:', filteredEvents);

        // Define event categories and their colors
        const eventCategories = {
            'TIME': { category: 'Times', color: '#000', y: 200 },
            'VITALS_TAKEN': { category: 'Vitals', color: '#000', y: 100 },
            'MEDICATION_GIVEN': { category: 'Medications', color: '#15c240', y: 150 },
            'PROCEDURE_PERFORMED': { category: 'Procedures', color: '#45b7d1', y: 50 },
            'DEVICE_EVENT': { category: 'Devices', color: '#96ceb4', y: 200 },
            'AIRWAY_CONFIRMATION': { category: 'Procedures', color: '#45b7d1', y: 200 },
            'CARDIAC_ARREST': { category: 'Procedures', color: '#45b7d1', y: 200 },
            'CPR_INITIATED': { category: 'Procedures', color: '#45b7d1', y: 200 },
            'RESUSCITATION_ENDED': { category: 'Procedures', color: '#45b7d1', y: 200 }
        };

        // Define phase ranges based on key events
        const phases = [];
        let currentPhase = null;

        // Find key events to determine phases
        const callBegin = filteredEvents.find(e => e.eventType === 'TIME' && e.data?.specification === 'PSAP_CALL');
        const dispatchBegin = filteredEvents.find(e => e.eventType === 'TIME' && e.data?.specification === 'DISPATCH_NOTIFIED');
        const onScene = filteredEvents.find(e => e.eventType === 'TIME' && e.data?.specification === 'UNIT_ON_SCENE');
        const leftScene = filteredEvents.find(e => e.eventType === 'TIME' && e.data?.specification === 'UNIT_LEFT_SCENE');
        const atDestination = filteredEvents.find(e => e.eventType === 'TIME' && e.data?.specification === 'PATIENT_AT_DESTINATION');
        const backInService = filteredEvents.find(e => e.eventType === 'TIME' && e.data?.specification === 'UNIT_IN_SERVICE');

        // if (callBegin && dispatchBegin) {
        //     phases.push({
        //         phase: 'Call Processing',
        //         start: callBegin.timeStamp,
        //         end: dispatchBegin.timeStamp,
        //         color: '#ffe6e6'
        //     });
        // }

        if (dispatchBegin && onScene) {
            phases.push({
                phase: 'Unit Notified to Scene',
                start: dispatchBegin.timeStamp,
                end: onScene.timeStamp,
                color: '#c742b1'
            });
        }

        if (onScene && leftScene) {
            phases.push({
                phase: 'On Scene',
                start: onScene.timeStamp,
                end: leftScene.timeStamp,
                color: '#2d76ba'
            });
        }

        if (leftScene && atDestination) {
            phases.push({
                phase: 'Transport',
                start: leftScene.timeStamp,
                end: atDestination.timeStamp,
                color: '#73bf2c'
            });
        }

        if (atDestination && backInService) {
            phases.push({
                phase: 'At Destination to Back in Service',
                start: atDestination.timeStamp,
                end: backInService.timeStamp,
                color: '#d62727'
            });
        }

        const spec = {
            "$schema": "https://vega.github.io/schema/vega-lite/v5.json",
            "width": "container",
            "height": height,
            "config": {
                "legend": {
                    "layout": {
                        "top": {
                            "margin": 100
                        }
                    },
                    "titleFontSize": 12,
                    "labelFontSize": 10
                }
            },
            "data": {
                "values": [
                    ...phases.map(p => ({
                        ...p,
                        startTime: new Date(p.start),
                        endTime: new Date(p.end)
                    })),
                    ...filteredEvents.filter(e => e.eventType !== 'TIME').map(e => {
                        const category = eventCategories[e.eventType] || { category: 'Other', color: '#cccccc', y: 300 };
                        
                        // Generate appropriate label based on event type
                        let label = '';
                        if (e.eventType === 'PROCEDURE_PERFORMED' && e.data?.procedure) {
                            label = e.data.procedure;
                        } else if (e.eventType === 'MEDICATION_GIVEN' && e.data?.medication) {
                            label = e.data.medication;
                        } else if (e.eventType === 'VITALS_TAKEN') {
                            label = ''; // No label for vitals
                        } else {
                            label = e.eventType; // Default to event type for other events
                        }
                        
                        return {
                            time: new Date(e.timeStamp),
                            event: e.eventType,
                            label: label,
                            category: category.category,
                            color: category.color,
                            y: category.y,
                            details: e.data ? JSON.stringify(e.data) : ''
                        };
                    })
                ]
            },
            "layer": [
                {
                    // Phase bands
                    "data": {
                        "values": phases.map(p => ({
                            ...p,
                            startTime: new Date(p.start),
                            endTime: new Date(p.end)
                        }))
                    },
                    "mark": {
                        "type": "rect",
                        "opacity": 0.3
                    },
                    "encoding": {
                        "x": {"field": "startTime", "type": "temporal"},
                        "x2": {"field": "endTime"},
                        "y": {"value": 0},
                        "y2": {"value": height},
                        "fill": {
                            "field": "phase",
                            "type": "nominal",
                            "scale": {
                                "domain": phases.map(p => p.phase),
                                "range": phases.map(p => p.color)
                            },
                            "legend": {
                                "title": "Response Phases",
                                "orient": "top",
                                "direction": "horizontal"
                            }
                        }
                    }
                },
                {
                    // Event points
                    "mark": {
                        "type": "circle",
                        "size": 120
                    },
                    "encoding": {
                        "x": {
                            "field": "time",
                            "type": "temporal",
                            "axis": {
                                "title": null,
                                "orient": "top",
                                "format": "%H:%M"
                            }
                        },
                        "y": {
                            "field": "y", 
                            "type": "quantitative",
                            "axis": {
                                "title": null,
                                "ticks": false,
                                "grid": false,
                                "labels": false
                            }
                        },
                        "color": {
                            "field": "category",
                            "type": "nominal",
                            "scale": {
                                "domain": ["Vitals", "Medications", "Procedures", "Other"],
                                "range": ["#ff6b6b", "#15c240", "#45b7d1", "#000"]
                            },
                            "legend": {
                                "title": "Event Types",
                                "orient": "top",
                                "direction": "horizontal"
                            }
                        },
                        "tooltip": [
                            // {"field": "event", "type": "nominal", "title": "Event"},
                            // {"field": "time", "type": "temporal", "title": "Time"},
                            {"field": "details", "type": "nominal", "title": "Details"}
                        ]
                    }
                },
                {
                    // Event labels
                    "mark": {
                        "type": "text",
                        "angle": 45,
                        "align": "left",
                        "baseline": "middle",
                        // "dy": -10,
                        "dx": 10
                    },
                    "encoding": {
                        "x": {
                            "field": "time", 
                            "type": "temporal",
                            "axis": {
                                "orient": "top",
                                "format": "%H:%M"
                            }
                        },
                        "y": {
                            "field": "y", 
                            "type": "quantitative",
                            "axis": {
                                "title": null,
                                "ticks": false,
                                "grid": false,
                                "labels": false
                            }
                        },
                        "text": {"field": "label"},
                        "size": {"value": 10}
                    }
                }
            ]
        };

        vegaEmbed(containerRef.current, spec, {
            actions: false,
            theme: 'light'
        });

    }, [reportData]);

    return <div ref={containerRef} style={{ width: '100%', height: `${height}px` }} />;
};

export default ReportTimeline;

